import React, {
  useContext, useEffect, useCallback, useRef,
} from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { AppContext } from '../../context/AppContext';
import { UserContext } from '../../context/UserContext';
import colors from '../../styles/colors';
import Icon from '../Icon';
import { IconEnum as Icons } from '../Icons';
import { useDesktopMedia } from '../../util/MediaQuery';
import { isGuidedOnboardingEnabled } from '../../util/guidedOnboarding';
import {
  getVisibleChecklistSteps,
  isStepComplete,
  getFirstIncompleteStepId,
  organizationSmsCampaignsEnabled,
} from './checklistConfig';

const LEFT_RAIL_PX = 80;

const Panel = styled.aside`
  position: fixed;
  top: 0;
  left: ${LEFT_RAIL_PX}px;
  width: min(360px, calc(100vw - ${LEFT_RAIL_PX}px));
  height: 100%;
  background: ${colors.white};
  box-shadow: 4px 0 24px rgba(0, 0, 0, 0.08);
  z-index: 850;
  display: flex;
  flex-direction: column;
  padding: 20px 16px 24px;
  box-sizing: border-box;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 20px;
  flex-shrink: 0;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 1.15rem;
  font-weight: 600;
  color: ${colors.grey2};
  line-height: 1.3;
`;

const CloseBtn = styled.button`
  background: transparent;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: ${colors.grey3};
  line-height: 0;
  flex-shrink: 0;
  &:hover {
    color: ${colors.grey2};
  }
`;

const ProgressTrack = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 20px;
  flex-shrink: 0;
`;

const ProgressDash = styled.div`
  flex: 1;
  height: 4px;
  border-radius: 2px;
  background: ${(p) => (p.$complete ? colors.seaGreen : colors.grey5)};
  transition: background 0.2s ease;
`;

const StepList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
`;

const StepRow = styled.li`
  display: flex;
  gap: 12px;
  padding: 14px 10px;
  margin-bottom: 4px;
  border-radius: 8px;
  cursor: pointer;
  border: 1px solid transparent;
  background: ${(p) => {
  if (p.$focused) return 'rgba(39, 187, 173, 0.08)';
  return 'transparent';
}};
  border-color: ${(p) => (p.$focused ? colors.seaGreen : 'transparent')};
  transition: background 0.15s ease, border-color 0.15s ease;
  &:hover {
    background: ${(p) => (p.$focused ? 'rgba(39, 187, 173, 0.12)' : colors.grey6)};
  }
`;

const StepIconCol = styled.div`
  flex-shrink: 0;
  width: 22px;
  display: flex;
  justify-content: center;
  padding-top: 2px;
`;

const StepTextCol = styled.div`
  flex: 1;
  min-width: 0;
`;

const StepTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${(p) => (p.$muted ? colors.grey3 : colors.grey2)};
  line-height: 1.35;
`;

const StepDescription = styled.div`
  font-size: 13px;
  font-style: italic;
  color: ${colors.grey3};
  margin-top: 4px;
  line-height: 1.4;
`;

const TourDeadEndBanner = styled.div`
  flex-shrink: 0;
  padding: 12px;
  margin-bottom: 12px;
  border-radius: 8px;
  background: ${colors.grey6};
  border: 1px solid ${colors.grey5};
  font-size: 13px;
  color: ${colors.grey2};
  line-height: 1.4;
`;

const RetryButton = styled.button`
  margin-top: 10px;
  padding: 8px 14px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  font-weight: 600;
  font-size: 13px;
  background: ${colors.seaGreen};
  color: ${colors.white};
  &:hover {
    opacity: 0.92;
  }
`;

/**
 * Account Setup Checklist (Phase C): seven steps (D-2), progress dashes (FR-3),
 * focus + navigation (§7.2, FR-6). Highlight tours / Continue widget = Phase D.
 */
export default function AccountSetupChecklistSidebar() {
  const history = useHistory();
  const { app, setApp } = useContext(AppContext);
  const { user } = useContext(UserContext);
  const isDesktop = useDesktopMedia();
  const open = !!app?.onboarding?.checklistSidebarOpen;
  const org = user?.organization;
  const progress = org?.onboardingChecklistProgress;
  const visibleSteps = getVisibleChecklistSteps(org);
  const focusedId = app?.onboarding?.focusedChecklistStepId ?? null;
  const tourMissStepId = app?.onboarding?.tourTargetMissStepId ?? null;
  const prevOrgIdRef = useRef();

  useEffect(() => {
    const orgId = user?.organization?._id;
    if (prevOrgIdRef.current && orgId && prevOrgIdRef.current !== orgId) {
      setApp((prev) => ({
        ...prev,
        onboarding: {
          ...prev.onboarding,
          focusedChecklistStepId: null,
          checklistDeferTourUntilStepClick: false,
          tourScreenIndex: 0,
          continueWidgetAfterStepId: null,
          tourTargetMissStepId: null,
          tourSuppressForStepId: null,
          tourRetryKey: 0,
        },
      }));
    }
    prevOrgIdRef.current = orgId;
  }, [user?.organization?._id, setApp]);

  useEffect(() => {
    if (!org || organizationSmsCampaignsEnabled(org)) return;
    if (focusedId !== 7) return;
    setApp((prev) => ({
      ...prev,
      onboarding: {
        ...prev.onboarding,
        focusedChecklistStepId: null,
        tourScreenIndex: 0,
        tourSuppressForStepId: null,
        tourTargetMissStepId: null,
      },
    }));
  }, [org, focusedId, setApp]);

  const close = useCallback(() => {
    setApp((prev) => ({
      ...prev,
      onboarding: {
        ...prev.onboarding,
        checklistSidebarOpen: false,
        checklistDeferTourUntilStepClick: false,
        focusedChecklistStepId: null,
        tourScreenIndex: 0,
      },
    }));
  }, [setApp]);

  useEffect(() => {
    if (!isDesktop && open) {
      setApp((prev) => ({
        ...prev,
        onboarding: { ...prev.onboarding, checklistSidebarOpen: false },
      }));
    }
  }, [isDesktop, open, setApp]);

  /** §7.2: first time opening in a session → focus first incomplete step (not when opened via Continue widget). */
  useEffect(() => {
    if (!open || !progress) return;
    if (focusedId != null) return;
    if (app?.onboarding?.checklistDeferTourUntilStepClick) return;
    const first = getFirstIncompleteStepId(progress, org);
    if (first == null) return;
    setApp((prev) => ({
      ...prev,
      onboarding: {
        ...prev.onboarding,
        focusedChecklistStepId: first,
        tourScreenIndex: 0,
      },
    }));
  }, [open, progress, org, focusedId, app?.onboarding?.checklistDeferTourUntilStepClick, setApp]);

  const handleStepClick = useCallback((step) => {
    if (!progress) return;
    setApp((prev) => ({
      ...prev,
      onboarding: {
        ...prev.onboarding,
        checklistSidebarOpen: false,
        checklistDeferTourUntilStepClick: false,
        focusedChecklistStepId: step.id,
        tourScreenIndex: 0,
        tourSuppressForStepId: null,
      },
    }));
    if (step.route) {
      const qs = step.settingsTab ? `?settingsTab=${step.settingsTab}` : '';
      history.push(`${step.route}${qs}`);
    }
  }, [history, progress, setApp]);

  if (!isGuidedOnboardingEnabled(user) || !open || !isDesktop || !progress) {
    return null;
  }

  return (
    <Panel role="complementary" aria-label="Account setup checklist">
      <HeaderRow>
        <Title>Account Setup Checklist</Title>
        <CloseBtn type="button" onClick={close} aria-label="Close checklist">
          <Icon icon={Icons.TimesCircleSolid} size={22} />
        </CloseBtn>
      </HeaderRow>

      <ProgressTrack aria-hidden>
        {visibleSteps.map((s) => (
          <ProgressDash
            key={s.id}
            $complete={isStepComplete(s.id, progress)}
          />
        ))}
      </ProgressTrack>

      {tourMissStepId != null && tourMissStepId === focusedId && (
        <TourDeadEndBanner role="status">
          We could not anchor every step of this tour on the page (for example after a slow load
          or a layout change). The checklist step stays incomplete until you finish the tour.
          <RetryButton
            type="button"
            onClick={() => {
              setApp((prev) => ({
                ...prev,
                onboarding: {
                  ...prev.onboarding,
                  tourTargetMissStepId: null,
                  tourScreenIndex: 0,
                  tourRetryKey: (prev.onboarding?.tourRetryKey ?? 0) + 1,
                },
              }));
            }}
          >
            Retry tour
          </RetryButton>
        </TourDeadEndBanner>
      )}

      <StepList>
        {visibleSteps.map((step) => {
          const complete = isStepComplete(step.id, progress);
          const focused = focusedId === step.id;
          return (
            <StepRow
              key={step.id}
              $focused={focused}
              onClick={() => handleStepClick(step)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleStepClick(step);
                }
              }}
            >
              <StepIconCol>
                {complete ? (
                  <Icon icon={Icons.CheckCircleSolid} size={18} active={1} activeColor={colors.seaGreen} />
                ) : (
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      border: `2px solid ${colors.grey4}`,
                      display: 'inline-block',
                    }}
                  />
                )}
              </StepIconCol>
              <StepTextCol>
                <StepTitle $muted={complete && !focused}>{step.title}</StepTitle>
                <StepDescription>{step.description}</StepDescription>
              </StepTextCol>
            </StepRow>
          );
        })}
      </StepList>
    </Panel>
  );
}
